import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, Upload, Loader2, AlertTriangle, Info, Lock, Sparkles,
  Trash2, Plus, FileText, ArrowRight, Music2, ExternalLink, X, BarChart3,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { royaltyDashboard, royaltyEntries, royaltyCreateEntry, royaltyDeleteEntry, royaltyStatements, royaltyDeleteStatement, royaltyUploadStatement } from '../lib/api';
import { useApp } from '../context/AppContext';

const SOURCE_LABEL = {
  distributor:        'Distributor (streaming)',
  pro_performance:    'PRO performance',
  mechanical:         'Mechanicals (MLC)',
  soundexchange:      'SoundExchange',
  sync:               'Sync placements',
  youtube_content_id: 'YouTube Content ID',
  merch:              'Merch',
  other:              'Other',
};
const SOURCE_COLOR = {
  distributor:        '#22d3ee',
  pro_performance:    '#ffcf52',
  mechanical:         '#a78bfa',
  soundexchange:      '#34d399',
  sync:               '#f472b6',
  youtube_content_id: '#ff0033',
  merch:              '#ff7a00',
  other:              '#94a3b8',
};
const SEVERITY = {
  high:   { color: '#ff4757', bg: 'rgba(255,71,87,0.1)',  border: 'rgba(255,71,87,0.4)' },
  medium: { color: '#ffcf52', bg: 'rgba(255,207,82,0.1)', border: 'rgba(255,207,82,0.4)' },
  low:    { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', border: 'rgba(34,211,238,0.4)' },
};

export default function Royalty() {
  const { user } = useApp();
  const tier = (user?.reapercast_tier || 'free').toLowerCase();
  const isAdmin = ['admin', 'owner'].includes(user?.role);
  const locked = tier !== 'studio' && !isAdmin;

  if (locked) return <RoyaltyLocked tier={tier} />;
  return <RoyaltyDashboard />;
}

function RoyaltyLocked({ tier }) {
  const isPro = tier === 'pro';
  return (
    <div className="max-w-[920px] mx-auto" data-testid="royalty-locked">
      <header className="mb-6">
        <div className="chip"><DollarSign className="w-3 h-3" /> Royalty Dashboard</div>
        <h1 className="font-display text-[36px] sm:text-[48px] md:text-[56px] mt-3 leading-[1.02]">Track <span className="brand-gradient">every dollar</span>. Catch every missing one.</h1>
        <p className="text-white/65 text-[14px] md:text-[16px] mt-3 max-w-[680px] leading-relaxed">
          Upload CSVs from any distributor, PRO, or SoundExchange — our AI auto-classifies every line. See unified totals, period-over-period trends, and red-flag alerts when royalties go missing.
        </p>
      </header>

      <section className="card-shell p-6 md:p-8 mb-7" style={{ background: 'linear-gradient(135deg, rgba(255,207,82,0.12), rgba(255,46,99,0.06))', borderColor: 'rgba(255,207,82,0.4)' }}>
        <div className="flex items-start gap-4 flex-wrap">
          <span className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(255,207,82,0.2)', color: '#ffcf52' }}>
            <Lock className="w-6 h-6" />
          </span>
          <div className="flex-1 min-w-[280px]">
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#ffcf52]">Studio tier · $19.99/mo</div>
            <h2 className="font-display text-[24px] mt-1">Unlock the Royalty Dashboard</h2>
            <p className="text-[13.5px] text-white/70 mt-2 leading-relaxed">
              {isPro
                ? "You're on Pro. Upgrade to Studio to unlock the Royalty Dashboard, AI statement parser, and missing-royalty alerts."
                : "Studio includes the full Royalty Dashboard, AI CSV parsing, anomaly detection, and 2 TB of video hosting."}
            </p>
            <Link to="/memberships" className="btn-primary mt-4 inline-flex" data-testid="royalty-upgrade-cta">
              <Sparkles className="w-4 h-4" /> Upgrade to Studio
            </Link>
          </div>
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-3" data-testid="royalty-teaser-grid">
        <TeaserCard Icon={Upload}  title="AI CSV parser"        desc="Upload any distributor / PRO / SoundExchange CSV. AI maps the columns automatically." color="#22d3ee" />
        <TeaserCard Icon={BarChart3} title="Unified earnings view" desc="Lifetime, period-over-period, by-source, by-track — all in one place." color="#a78bfa" />
        <TeaserCard Icon={AlertTriangle} title="Missing-royalty alerts" desc="Auto-flags when SoundExchange, MLC, or PRO royalties are absent or sudden drops occur." color="#ff4757" />
        <TeaserCard Icon={TrendingUp} title="Sync + opportunity nudges" desc="Spot which income streams are silent and act on them." color="#34d399" />
      </section>

      <p className="text-[11.5px] text-white/45 text-center mt-7">All your statements stay private. We never share data with distributors or third parties.</p>
    </div>
  );
}

function TeaserCard({ Icon, title, desc, color }) {
  return (
    <div className="card-shell p-4">
      <span className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: color + '22', color }}><Icon className="w-5 h-5" /></span>
      <h3 className="font-display text-[15px] mt-3">{title}</h3>
      <p className="text-[12.5px] text-white/55 mt-1.5 leading-relaxed blur-[1.2px] select-none">{desc}</p>
    </div>
  );
}

function RoyaltyDashboard() {
  const [data, setData] = useState(null);
  const [statements, setStatements] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s, e] = await Promise.all([royaltyDashboard(), royaltyStatements(), royaltyEntries()]);
      setData(d); setStatements(s.items || []); setEntries(e.items || []);
    } catch { toast.error('Could not load royalty dashboard'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!data) return null;

  return (
    <div className="max-w-[1280px] mx-auto" data-testid="royalty-dashboard">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="chip" style={{ color: '#34d399', borderColor: 'rgba(52,211,153,0.4)' }}><DollarSign className="w-3 h-3" /> Royalty Dashboard</div>
          <h1 className="font-display text-[34px] sm:text-[42px] mt-2 leading-tight">Every dollar, every source, in one view.</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowManual(true)} className="btn-ghost text-[13px]" data-testid="royalty-add-manual"><Plus className="w-4 h-4" /> Add manual</button>
          <button onClick={() => setShowUpload(true)} className="btn-primary text-[13px]" data-testid="royalty-upload-cta"><Upload className="w-4 h-4" /> Upload statement</button>
        </div>
      </header>

      <SummaryTiles totals={data.totals} bySource={data.by_source} />
      <Alerts alerts={data.alerts} />

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4 mt-5">
        <PeriodChart bySource={data.by_source} byPeriod={data.by_period} />
        <SourcePie bySource={data.by_source} />
      </div>

      <TopTracks rows={data.top_tracks} />
      <Statements items={statements} onChanged={load} />
      <EntriesTable items={entries} onChanged={load} />

      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {showManual && <ManualDialog onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); load(); }} />}
    </div>
  );
}

function SummaryTiles({ totals, bySource }) {
  const top = bySource[0];
  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="royalty-summary">
      <Tile label="Lifetime tracked" value={'$' + totals.lifetime_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })} icon={DollarSign} color="#34d399" />
      <Tile label="Total entries"     value={totals.entry_count.toLocaleString()} icon={FileText} color="#22d3ee" />
      <Tile label="Income streams"    value={bySource.length} icon={TrendingUp} color="#a78bfa" sub={`out of ${Object.keys(SOURCE_LABEL).length}`} />
      <Tile label="Top source"        value={top ? '$' + top.total_usd.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'} icon={BarChart3} color={top ? SOURCE_COLOR[top.source] : '#94a3b8'} sub={top ? SOURCE_LABEL[top.source] : 'No data'} />
    </section>
  );
}

function Tile({ label, value, icon: Ic, color, sub }) {
  return (
    <div className="card-shell p-4">
      <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: color + '22', color }}><Ic className="w-4 h-4" /></div>
      <div className="mt-2 font-display text-[24px] leading-none">{value}</div>
      <div className="text-[11.5px] text-white/55 mt-1">{label}{sub && <> · <span className="text-white/40">{sub}</span></>}</div>
    </div>
  );
}

function Alerts({ alerts }) {
  if (!alerts?.length) return null;
  return (
    <section className="mt-5" data-testid="royalty-alerts">
      <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-[#ffcf52]" /><h3 className="row-title text-white">Alerts &amp; opportunities ({alerts.length})</h3></div>
      <ul className="grid gap-2">
        {alerts.map((a, i) => {
          const sev = SEVERITY[a.severity] || SEVERITY.low;
          const isInternal = a.href?.startsWith('/');
          return (
            <li key={i} className="card-shell p-3.5 flex items-start gap-3" style={{ borderColor: sev.border, background: sev.bg }} data-testid={`royalty-alert-${a.kind}`}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: sev.color }} />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] leading-relaxed text-white/80">{a.message}</p>
                {a.cta && (
                  isInternal
                    ? <Link to={a.href} className="text-[12px] font-semibold mt-1.5 inline-flex items-center gap-1" style={{ color: sev.color }}>{a.cta} <ArrowRight className="w-3 h-3" /></Link>
                    : <a href={a.href} target="_blank" rel="noreferrer noopener" className="text-[12px] font-semibold mt-1.5 inline-flex items-center gap-1" style={{ color: sev.color }}>{a.cta} <ExternalLink className="w-3 h-3" /></a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PeriodChart({ bySource, byPeriod }) {
  if (!byPeriod?.length) {
    return (
      <div className="card-shell p-5" data-testid="royalty-empty-chart">
        <h3 className="row-title text-white">Earnings by period</h3>
        <div className="h-[260px] grid place-items-center text-white/40 text-[13px] text-center">
          <div>
            <BarChart3 className="w-7 h-7 mx-auto mb-2 opacity-40" />
            Upload a statement to see your earnings chart.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="card-shell p-5">
      <h3 className="row-title text-white">Earnings by period</h3>
      <div style={{ width: '100%', height: 280 }} className="mt-2">
        <ResponsiveContainer>
          <BarChart data={byPeriod}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="period" stroke="rgba(255,255,255,0.45)" fontSize={11} />
            <YAxis stroke="rgba(255,255,255,0.45)" fontSize={11} tickFormatter={v => '$' + v} />
            <Tooltip contentStyle={{ background: '#1a1a1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }} formatter={v => '$' + Number(v).toFixed(2)} />
            <Bar dataKey="total_usd" fill="#22d3ee" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SourcePie({ bySource }) {
  if (!bySource?.length) {
    return (
      <div className="card-shell p-5" data-testid="royalty-empty-pie">
        <h3 className="row-title text-white">Source mix</h3>
        <div className="h-[260px] grid place-items-center text-white/40 text-[13px]">No data yet</div>
      </div>
    );
  }
  return (
    <div className="card-shell p-5">
      <h3 className="row-title text-white">Source mix</h3>
      <div style={{ width: '100%', height: 280 }} className="mt-2">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={bySource} dataKey="total_usd" nameKey="source" outerRadius={90} innerRadius={50}>
              {bySource.map((s, i) => <Cell key={i} fill={SOURCE_COLOR[s.source] || '#94a3b8'} />)}
            </Pie>
            <Tooltip contentStyle={{ background: '#1a1a1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }} formatter={(v, n) => [`$${Number(v).toFixed(2)}`, SOURCE_LABEL[n] || n]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 grid gap-1">
        {bySource.map(s => (
          <li key={s.source} className="flex items-center justify-between text-[12px]">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: SOURCE_COLOR[s.source] }} /> {SOURCE_LABEL[s.source] || s.source}</span>
            <span className="font-mono text-white/70">${s.total_usd.toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TopTracks({ rows }) {
  if (!rows?.length) return null;
  return (
    <section className="mt-5">
      <div className="flex items-center gap-2 mb-2"><Music2 className="w-4 h-4 text-white/55" /><h3 className="row-title text-white">Top earning tracks</h3></div>
      <div className="card-shell overflow-hidden">
        <table className="w-full text-[13px]" data-testid="royalty-top-tracks">
          <thead className="bg-white/[0.03] text-white/55 text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-2.5">#</th><th className="text-left px-3 py-2.5">Track</th><th className="text-right px-3 py-2.5">Lines</th><th className="text-right px-4 py-2.5">Total</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.track_title} className="border-t border-white/5">
                <td className="px-4 py-2.5 text-white/55">{i + 1}</td>
                <td className="px-3 py-2.5 font-semibold">{r.track_title}</td>
                <td className="px-3 py-2.5 text-right text-white/55">{r.count}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[#34d399]">${r.total_usd.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Statements({ items, onChanged }) {
  if (!items?.length) return null;
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.filename}" and all its entries?`)) return;
    try { await royaltyDeleteStatement(s.id); toast.success('Deleted'); onChanged(); }
    catch { toast.error('Delete failed'); }
  };
  return (
    <section className="mt-5">
      <h3 className="row-title text-white mb-2">Uploaded statements</h3>
      <div className="card-shell overflow-hidden">
        <table className="w-full text-[13px]" data-testid="royalty-statements">
          <thead className="bg-white/[0.03] text-white/55 text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-2.5">File</th><th className="text-left px-3 py-2.5 hidden sm:table-cell">Source</th><th className="text-left px-3 py-2.5 hidden md:table-cell">Period</th><th className="text-right px-3 py-2.5">Rows</th><th className="text-right px-3 py-2.5">Total</th><th className="px-3 py-2.5"></th></tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="px-4 py-2.5 truncate max-w-[220px]"><FileText className="w-3.5 h-3.5 inline mr-1.5 text-white/40" />{s.filename}</td>
                <td className="px-3 py-2.5 hidden sm:table-cell text-white/65">{SOURCE_LABEL[s.source] || s.source}</td>
                <td className="px-3 py-2.5 hidden md:table-cell text-white/55">{s.period || '—'}</td>
                <td className="px-3 py-2.5 text-right">{s.entry_count}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[#34d399]">${s.total_usd.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right"><button onClick={() => remove(s)} className="text-white/40 hover:text-[#ff6b8a]" title="Delete" data-testid={`royalty-statement-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EntriesTable({ items, onChanged }) {
  const [filter, setFilter] = useState('');
  const filtered = useMemo(() => filter ? items.filter(e => e.source === filter) : items, [items, filter]);
  if (!items?.length) return (
    <section className="mt-5">
      <h3 className="row-title text-white mb-2">Entries</h3>
      <div className="card-shell p-8 text-center text-white/45 text-[13px]" data-testid="royalty-entries-empty">
        No entries yet. Upload a statement or add manual entries to build your dashboard.
      </div>
    </section>
  );
  const remove = async (e) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await royaltyDeleteEntry(e.id); toast.success('Deleted'); onChanged(); }
    catch { toast.error('Delete failed'); }
  };
  const sources = Array.from(new Set(items.map(e => e.source)));
  return (
    <section className="mt-5">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="row-title text-white">Entries ({filtered.length})</h3>
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setFilter('')} className={`chip text-[11px] ${filter === '' ? 'active' : ''}`}>All</button>
          {sources.map(s => <button key={s} onClick={() => setFilter(s)} className={`chip text-[11px] ${filter === s ? 'active' : ''}`}>{SOURCE_LABEL[s] || s}</button>)}
        </div>
      </div>
      <div className="card-shell overflow-hidden max-h-[420px] overflow-y-auto">
        <table className="w-full text-[12.5px]" data-testid="royalty-entries-table">
          <thead className="bg-white/[0.03] text-white/55 text-[11px] uppercase tracking-wider sticky top-0">
            <tr><th className="text-left px-3 py-2">Track</th><th className="text-left px-2 py-2 hidden sm:table-cell">Source</th><th className="text-left px-2 py-2 hidden md:table-cell">Period</th><th className="text-right px-2 py-2">Amount</th><th className="px-2 py-2"></th></tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map(e => (
              <tr key={e.id} className="border-t border-white/5">
                <td className="px-3 py-1.5 truncate max-w-[220px]">{e.track_title || <span className="text-white/35">—</span>}</td>
                <td className="px-2 py-1.5 hidden sm:table-cell text-white/65" style={{ color: SOURCE_COLOR[e.source] }}>{SOURCE_LABEL[e.source] || e.source}</td>
                <td className="px-2 py-1.5 hidden md:table-cell text-white/55">{e.period || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono text-[#34d399]">${e.amount_usd.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right"><button onClick={() => remove(e)} className="text-white/40 hover:text-[#ff6b8a]"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UploadDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [sourceHint, setSourceHint] = useState('');
  const [periodHint, setPeriodHint] = useState('');
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) { toast.error('Pick a CSV file first'); return; }
    setBusy(true);
    try {
      const r = await royaltyUploadStatement(file, sourceHint || null, periodHint || null);
      toast.success(`Imported ${r.inserted} entries${r.skipped ? ` · skipped ${r.skipped}` : ''}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-3" onClick={onClose}>
      <div className="card-shell w-full max-w-[520px] p-5" onClick={e => e.stopPropagation()} data-testid="royalty-upload-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-[20px]">Upload royalty statement</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center rounded-full bg-white/5 hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[12.5px] text-white/60 mb-4">Drop any CSV from a distributor, PRO, or SoundExchange. Our AI auto-maps the columns.</p>

        <label className="block mb-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-white/55 mb-1.5">CSV file <span className="text-white/30">(max 8 MB)</span></div>
          <input type="file" accept=".csv,.tsv,.txt" onChange={e => setFile(e.target.files?.[0] || null)} className="input-base file:mr-2 file:px-3 file:py-1 file:rounded-md file:border-0 file:bg-white/10 file:text-white file:cursor-pointer" data-testid="royalty-file-input" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider font-bold text-white/55 mb-1.5">Source hint</div>
            <select value={sourceHint} onChange={e => setSourceHint(e.target.value)} className="input-base">
              <option value="">— let AI guess —</option>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider font-bold text-white/55 mb-1.5">Period hint</div>
            <input value={periodHint} onChange={e => setPeriodHint(e.target.value)} placeholder="e.g. 2026-Q1" className="input-base" />
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost text-[12.5px]">Cancel</button>
          <button onClick={upload} disabled={busy || !file} className="btn-primary text-[12.5px]" data-testid="royalty-upload-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload &amp; parse
          </button>
        </div>
        <p className="text-[10.5px] text-white/40 mt-3"><Info className="w-3 h-3 inline mr-1" />Your statement stays private — never shared with distributors or third parties.</p>
      </div>
    </div>
  );
}

function ManualDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ source: 'distributor', track_title: '', track_isrc: '', period: '', amount_usd: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.amount_usd) { toast.error('Amount required'); return; }
    setBusy(true);
    try {
      await royaltyCreateEntry({ ...form, amount_usd: parseFloat(form.amount_usd) });
      toast.success('Added'); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-3" onClick={onClose}>
      <div className="card-shell w-full max-w-[480px] p-5" onClick={e => e.stopPropagation()} data-testid="royalty-manual-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-[20px]">Add manual entry</h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center rounded-full bg-white/5 hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <Field label="Source">
          <select value={form.source} onChange={e => set('source', e.target.value)} className="input-base">
            {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Amount (USD)"><input type="number" step="0.01" value={form.amount_usd} onChange={e => set('amount_usd', e.target.value)} className="input-base" placeholder="0.00" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Track title (optional)"><input value={form.track_title} onChange={e => set('track_title', e.target.value)} className="input-base" /></Field>
          <Field label="Period (optional)"><input value={form.period} onChange={e => set('period', e.target.value)} className="input-base" placeholder="2026-Q1" /></Field>
        </div>
        <Field label="ISRC (optional)"><input value={form.track_isrc} onChange={e => set('track_isrc', e.target.value)} className="input-base" placeholder="US-XXX-25-00001" /></Field>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="btn-ghost text-[12.5px]">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary text-[12.5px]" data-testid="royalty-manual-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add entry
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-[11px] uppercase tracking-wider font-bold text-white/55 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
